import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import { usePageInfo, useRaidPlans } from 'app/frontend/base/config/queries'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import RaidPlanCandidatePicker from './RaidPlanCandidatePicker'
import { CopyIcon } from 'lucide-react'

type CopyFromPlanPopoverProps = {
  owner: RaidPlanOwner
  planListPathPrefix: string
  currentPlan: RaidPlan
  buttonLabel: string
  emptyLabel: string
  onSelectPlan: (plan: RaidPlan) => void
}

const CopyFromPlanPopover = ({
  owner,
  planListPathPrefix,
  currentPlan,
  buttonLabel,
  emptyLabel,
  onSelectPlan,
}: CopyFromPlanPopoverProps) => {
  const {
    data: { currentSeason },
  } = usePageInfo()
  const [open, setOpen] = useState(false)
  const { data: teamPlans } = useRaidPlans(owner, currentSeason?.id, open)
  const { data: personalPlans } = useRaidPlans(
    owner.ownerType === 'team' ? { ownerType: 'user' } : undefined,
    currentSeason?.id,
    open,
  )

  const candidatePlans = useMemo(() => {
    return [...(teamPlans ?? []), ...(personalPlans ?? [])]
      .filter(
        (plan) =>
          plan.encounterId === currentPlan.encounterId &&
          !(
            plan.id === currentPlan.id &&
            plan.ownerType === currentPlan.ownerType
          ),
      )
      .sort((left, right) => {
        if (left.difficulty !== right.difficulty) {
          if (left.difficulty === currentPlan.difficulty) return -1
          if (right.difficulty === currentPlan.difficulty) return 1
          return difficultiesLabels[left.difficulty].localeCompare(
            difficultiesLabels[right.difficulty],
          )
        }
        return left.name.localeCompare(right.name)
      })
  }, [
    currentPlan.difficulty,
    currentPlan.encounterId,
    currentPlan.id,
    currentPlan.ownerType,
    teamPlans,
    personalPlans,
  ])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className='h-32 w-52 flex flex-col gap-3 items-center justify-center'
          variant='outline'
        >
          <CopyIcon className='w-8 h-8' />
          {buttonLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-fit max-w-[90vw]'>
        <div className='space-y-2'>
          <RaidPlanCandidatePicker
            plans={candidatePlans}
            warningDifficulty={currentPlan.difficulty}
            showOwnerBadge
            teamPath={planListPathPrefix}
            onSelect={(plan) => {
              onSelectPlan(plan)
              setOpen(false)
            }}
            emptyLabel={emptyLabel}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default CopyFromPlanPopover
